/**
 * @typedef {Object} VariantInventory
 * @property {number} id
 * @property {number} inventory_quantity
 */

class VariantSelects extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("change", this.onVariantChange);
    this.productId = this.dataset.productId;
    this.extendedOfferEnabled = this.dataset.extendedOfferEnabled;

    // switches
    this.shipsByBanner = {
      hide: () => {
        /** @type {HTMLElement | null} */
        const shipsByNotice = document.querySelector("#product-shipsby-notice");
        if (shipsByNotice) {
          shipsByNotice.style.display = "none";
        }
      },
      show: () => {
        /** @type {HTMLElement | null} */
        const shipsByNotice = document.querySelector("#product-shipsby-notice");
        if (shipsByNotice) {
          shipsByNotice.style.display = "flex";
        }
      },
    };

    // Add initialization of buyButtonText here
    this.buyButtonText = {
      addToCart: "Add to Cart",
      soldOut: "Sold Out",
      extendedOffer: "Extended Offer",
      clickToBuy: "Click to Buy",
    };
  }

  connectedCallback() {
    // Update with dataset values if provided
    this.buyButtonText = {
      addToCart: this.dataset.textAddToCart || this.buyButtonText.addToCart,
      soldOut: this.dataset.textSoldout || this.buyButtonText.soldOut,
      extendedOffer:
        this.dataset.textExtendedOffer || this.buyButtonText.extendedOffer,
      clickToBuy: this.dataset.textClickToBuy || this.buyButtonText.clickToBuy,
    };

    /**
     * @type {HTMLScriptElement | null}
     */
    const productElement = this.querySelector("script[id='product-json']");
    if (!productElement) {
      console.error("Could not find product json element");
      return;
    }
    this.product = JSON.parse(productElement.innerHTML);

    this.productType = this.dataset.productType;
    this.isGiftCard = this.productType === "Gift Card";

    const inventoryElement = this.querySelector(
      "script[id='variant-inventory']"
    );
    if (!inventoryElement) {
      console.error("Could not find variant inventory element");
      return;
    }
    this.variantInventory = JSON.parse(inventoryElement.innerHTML);
    // console.log("variant-inventory: ", this.variantInventory);

    /**
     * @type {HTMLElement | null}
     * */
    this.section = this.closest("section");
    if (!this.section) {
      console.error("Could not find section element for variant-selects");
      return;
    }
    this.sectionId = this.section.classList[0].replace("product-section--", "");
    /**
     * @type {HTMLInputElement | null}
     * */
    this.skuElement = this.section.querySelector("input[name='sku']");

    /**
     * @type {HTMLButtonElement | null}
     * */
    this.addToCartButton = this.section.querySelector(
      `#product-form-${this.sectionId} .product-form__submit`
    );

    /**
     * @type {HTMLButtonElement | null}
     * */
    this.extendedOfferButton = this.section.querySelector(
      `#extended-offer-button-${this.sectionId}`
    );

    const firstAvailableVariant = this.product.variants.find(
      /** @param {Variant} variant */ (variant) => variant.available
    );

    messenger.listen(messenger.topics.RESET_PRODUCT_FORM, () => {
      const firstAvailableVariant = this.getFirstAvailableVariant();
      this.initUi(firstAvailableVariant);
    });

    window.addEventListener("message", (event) => {
      if (
        event.data.type === "ships_by_notification_ready" &&
        event.data.productId === this.product.id.toString()
      ) {
        this.initUi();
      }
    });

    // this.initUi(firstAvailableVariant);
  }

  getFirstAvailableVariant() {
    return (
      this.product.variants.find(
        /** @param {Variant} variant */ (variant) => variant.available
      ) || this.product.variants[0]
    );
  }

  // handlers

  onVariantChange() {
    this.updateCurrentVariant();
    this.updateURL();
    this.updateVariantInput();
    this.renderVariantPrice();
    this.setCta();
    this.postMessageVariantChanged();
  }

  setCta() {
    if (this.currentVariant.inventoryQuantity > 0 || this.isGiftCard) {
      this.setAddToCartUI();
    } else if (this.extendedOfferEnabled) {
      this.setExtendedOfferUI();
    } else {
      this.setSoldOutUI();
    }
  }

  /**
   * @param {boolean} show
   * @param {boolean} [withOverlap=false] - Optional parameter to add overlap class
   */
  toggleSoldOutElement(show, withOverlap = false) {
    if (!this.section) {
      console.error("Could not find section element for variant-selects");
      return;
    }
    /** @type {HTMLElement | null} */
    const soldOutElement = this.section.querySelector('.product__sold-out');
    if (!soldOutElement) return;

    soldOutElement.style.display = show ? 'flex' : 'none';
    
    if (show && withOverlap && this.section.querySelector('ships-by-notification')) {
      soldOutElement.classList.add('overlap');
    } else {
      soldOutElement.classList.remove('overlap');
    }
  }

  setAddToCartUI() {
    // console.log("setAddToCartUI");
    if (!this.addToCartButton) {
      console.error("Could not find add to cart button");
      return;
    }
    this.toggleExtendedOfferButton(false);
    this.setShipsByVisibility(true);
    this.addToCartButton.textContent = this.buyButtonText.addToCart;
    this.addToCartButton.removeAttribute("disabled");
    this.addToCartButton.type = "submit";

    this.toggleSoldOutElement(false);
  }

  setExtendedOfferUI() {
    // console.log("setExtendedOfferUI");
    this.toggleExtendedOfferButton(true);
    this.setHolidayShippingVisibility(false);
    this.setShipsByVisibility(false);

    this.toggleSoldOutElement(true, true);
  }

  setSoldOutUI() {
    // console.log("setSoldOutUI");
    if (!this.addToCartButton) {
      console.error("Could not find add to cart button");
      return;
    }
    this.setHolidayShippingVisibility(false);
    this.setShipsByVisibility(false);
    this.addToCartButton.textContent = this.buyButtonText.soldOut;
    this.addToCartButton.removeAttribute("disabled");
    
    this.toggleSoldOutElement(true, true);
  }

  /** @param {boolean} visible */
  setShipsByVisibility(visible) {
    /** @type {Array<HTMLElement> | null} */
    const shipsByElementsArray = Array.from(
      document.querySelectorAll("ships-by-notification")
    );
    if (!shipsByElementsArray || shipsByElementsArray.length === 0) return;
    const shipsByElements = shipsByElementsArray.filter((item) => {
      const section = item.getAttribute("data-section");
      return section && section.includes("main");
    });
    if (!shipsByElements) return;
    if (visible) {
      shipsByElements.forEach((element) => {
        element.style.display = "block";
      });
    } else {
      shipsByElements.forEach((element) => {
        element.style.display = "none";
      });
    }
  }

  /** @param {boolean} visible */
  setHolidayShippingVisibility(visible) {
    /** @type {HTMLElement | null} */
    const holidayShippingElement = document.querySelector(
      "holiday-shipping-countdown"
    );
    if (!holidayShippingElement) return;
    if (visible) {
      holidayShippingElement.style.display = "block";
    } else {
      holidayShippingElement.style.display = "none";
    }
  }

  /**
   *
   * @param {Variant | null} variant
   * @returns
   */
  initUi(variant = null) {
    if (!variant) {
      variant =
        this.getVariantFromUrl() ||
        this.getFirstAvailableVariant() ||
        this.product.variants[0];
    }
    if (!variant) {
      console.error("Could not find a variant to initialize the UI");
      return;
    }
    this.setVariantSelectsToValues(variant.options);
    this.setShipsBy(this.product.tags);

    const event = new Event("change", {
      bubbles: true,
    });

    this.dispatchEvent(event);
  }

  /**
   *
   * @param {string[]} tags
   */
  setShipsBy(tags) {
    const shipsByObject = knocking.getTagShippingDate(tags);
    this.postMessageShipsBy(shipsByObject.shortDate);
    /**
     * @type {HTMLInputElement | null}
     * */
    let shipsByInput;
    /**
     * @type {HTMLElement | null}
     * */
    const section = this.closest("section");

    shipsByInput = section ? section.querySelector("#ships-by-input") : null;
    if (shipsByInput) {
      shipsByInput.value = shipsByObject.longDate;
    }
  }

  /**
   *
   * @param {string[]} options
   */
  setVariantSelectsToValues(options) {
    const selects = Array.from(this.querySelectorAll("select"));

    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      selects[i].value = option;
    }
  }

  postMessageVariantChanged() {
    // When variant is changed post a message with the variant's data
    messenger.emmit(messenger.topics.VARIANT_CHANGE, this.currentVariant);
  }

  /**
   *
   * @param {string} shippingDate
   */
  postMessageShipsBy(shippingDate) {
    window.postMessage({
      type: "update_ships_by_notification",
      productId: this.productId,
      shippingDate: shippingDate,
    });
  }

  /**
   *
   * @param {Boolean} show
   */
  toggleExtendedOfferButton(show) {
    // show extended offer button if exists
    if (this.extendedOfferButton) {
      this.extendedOfferButton.style.display = show ? "flex" : "none";
    }
    if (this.addToCartButton) {
      this.addToCartButton.style.display = show ? "none" : "flex";
    }
  }

  renderVariantPrice() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const destination = document.getElementById(id);
        const source = html.getElementById(id);

        if (source && destination) destination.innerHTML = source.innerHTML;

        const price = document.getElementById(`price-${this.dataset.section}`);
        if (price) {
          price.classList.remove("visibility-hidden");
        }
      });
  }

  // update
  getVariantFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get("variant");
    if (!variantId) return null;
    const variant = this.product.variants.find(
      /** @param {Variant} variant */ (variant) =>
        variant.id.toString() == variantId
    );
    if (!variant) return null;
    return variant;
  }

  updateCurrentVariant(variant = null) {
    this.options = Array.from(
      this.querySelectorAll("select"),
      (select) => select.value
    );
    const options = this.options;
    if (!options) {
      console.error("Can not determine variant-options");
      return;
    }
    if (variant) {
      this.currentVariant = variant;
    } else {
      this.currentVariant = this.product.variants.find(
        /** @param {Variant} variant */ (variant) => {
          return !variant.options
            .map((option, index) => {
              return options[index] === option;
            })
            .includes(false);
        }
      );

      const inventory = this.variantInventory.find(
        /** @param {VariantInventory} inventory */ (inventory) => {
          return inventory.id === this.currentVariant.id;
        }
      );
      this.currentVariant.inventoryQuantity = inventory.inventory_quantity;
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;
    window.history.replaceState(
      {},
      "",
      `${this.dataset.url}?variant=${this.currentVariant.id}`
    );
  }

  /**
   * @param {Variant | null} variant
   */
  updateVariantInput(variant = null) {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );

    productForms.forEach((productForm) => {
      /**
       * @type {HTMLInputElement | null}
       * */
      const input = productForm.querySelector('input[name="id"]');
      if (!input) {
        console.log("Could not find input element, aborting...");
        return;
      }

      /**
       * @type {HTMLInputElement | null}
       * */
      const skuElement = productForm.querySelector('input[name="sku"]');
      if (!skuElement) {
        console.log(
          "Could not find sku element, might not be rendered yet...?"
        );
      }

      if (variant) {
        input.value = variant.id.toString();
        if (skuElement) {
          skuElement.value = variant.sku;
        }
      } else {
        input.value = this.currentVariant.id;
        if (skuElement) {
          skuElement.value = this.currentVariant.sku;
        }
      }
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  // not in use
  /**
   * @param {string} name
   * @param {any} value
   * @param {boolean} visible
   */
  appendProperty(name, value, visible = false) {
    let productForm = document.querySelector(
      'form[id^="product-form-template"]'
    );
    if (!productForm) {
      console.log("Can not find product form, aborting...");
      return;
    }

    let propertyInput = document.createElement("input");
    let nameProperty;
    if (visible) {
      nameProperty = `properties[${name}]`;
    } else {
      nameProperty = `properties[_${name}]`;
    }
    propertyInput.setAttribute("type", "hidden");
    propertyInput.setAttribute("name", nameProperty);
    propertyInput.setAttribute("value", value.toString());
    productForm.appendChild(propertyInput);
  }

  cleanUpPropertyInputs() {
    const productForm = document.querySelector(
      'form[id^="product-form-template"]'
    );
    if (!productForm) return;
    const inputs = Array.from(productForm.querySelectorAll("input"));
    const propertyInputs = inputs.filter((input) => {
      const name = input.getAttribute("name");
      if (name && name.includes("properties")) {
        return input;
      }
    });
    propertyInputs.forEach((input) => {
      input.remove();
    });
  }
}

customElements.define("variant-selects", VariantSelects);
